import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/api";

export function useConnectionStatus() {
  const [openAIConnected, setOpenAIConnected] = useState<boolean | null>(null);
  const [supabaseConnected, setSupabaseConnected] = useState<boolean | null>(null);
  const [openAIError, setOpenAIError] = useState<string | null>(null);
  const [supabaseError, setSupabaseError] = useState<string | null>(null);

  const checkOpenAI = useCallback(async () => {
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) {
        setOpenAIConnected(false);
        setOpenAIError("No token");
        return;
      }
      const res = await apiFetch("/v1/openai/check", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setOpenAIConnected(true);
        setOpenAIError(null);
      } else {
        setOpenAIConnected(false);
        setOpenAIError(res.statusText || `Error ${res.status}`);
      }
    } catch (e) {
      setOpenAIConnected(false);
      setOpenAIError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const checkSupabase = useCallback(async () => {
    try {
      const { error } = await supabase.auth.getSession();
      if (error) {
        setSupabaseConnected(false);
        setSupabaseError(error.message);
      } else {
        setSupabaseConnected(true);
        setSupabaseError(null);
      }
    } catch (e) {
      setSupabaseConnected(false);
      setSupabaseError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const refresh = useCallback(() => {
    checkOpenAI();
    checkSupabase();
  }, [checkOpenAI, checkSupabase]);

  useEffect(() => {
    refresh();
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      refresh();
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [refresh]);

  return {
    openAIConnected,
    supabaseConnected,
    refresh,
    checkOpenAI,
    checkSupabase,
    openAIError,
    supabaseError,
  };
}
